import React, { useState, useEffect, useMemo, FC, ChangeEvent, useRef } from 'react';
import { DefaultList as List } from 'services/UIUtils';
import SelectableListItem from 'components/Utils/SelectableListItem';
import './style.scss';
import moment from 'moment-timezone';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import UnreadBadge from 'components/Common/UnreadBadge';
import { Fax, PatientDocument, TaskComment, ReferralRequest } from 'generated/api';
import useFetchPatients from 'hooks/useFetchPatients';
import {
  ReferralRequestBadge,
  ReferralRequestDetails,
} from 'scenes/HomeStaff/ReferralsDirectory/ReferralRequest';
import { REFERRAL_STATUS } from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants';
import DocumentListWithDetails from 'components/HealthRecords/Documents/DocumentListWithDetails';
import PhysicianBadge from 'components/Physician/PhysicianBadge';
import PatientBadge from 'components/Patient/PatientBadge';
import ContactBadge from 'components/Contacts/ContactBadge';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultTextField from 'components/Utils/DefaultTextField';
import usePersistedState from 'hooks/usePersistedState';
import Icon from 'components/Utils/NewIcon';
import StaffUtils from 'services/StaffUtils';
import StringUtils from 'services/StringUtils';
import CalendarUtils from 'services/CalendarUtils';
import ContentEditable from 'components/Utils/ContentEditable';
import DefaultModal from 'components/Utils/DefaultModal';
import NewReferralAdmin from 'components/HealthRecords/Documents/NewReferralAdmin';
import IconButton from '@material-ui/core/IconButton';
import { uiActions } from 'careand-redux/slices/ui/ui';
import SmallResourcePreview from 'components/Utils/SmallResourcePreview';
import SendMultipleDocuments from 'components/HealthRecords/Documents/SendMultipleDocuments';
import { FaxPreview } from 'components/Patient/CPP/CPPTimeline/CPPTimelineFax';
import { NumberBadge } from 'scenes/HomeStaff/ClinicQueue/QueueBadge';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import DocumentDetailsModal from 'components/HealthRecords/Documents/DocumentDetailsModal';
import { GetStaff } from 'careand-redux/selectors';
import PatientUtils from 'services/PatientUtils';
import ProgressMessage from 'components/Utils/ProgressMessage';
import ChatUtils from 'services/ChatUtils';
import GenericDirectory from 'components/Directories/GenericDirectory';
import ResponsiveColumns, { NavigationRef } from 'components/ResponsiveColumns';
import ListWithDetails from 'components/Common/ListWithDetails';
import ConfirmationButton from '../../../components/Utils/NewDefaultButton/ConfirmationButton';

const OPTIONS = {
  pending_action: {
    requests: 'Requests',
    pending_send: REFERRAL_STATUS.pending_send,
    overdue: REFERRAL_STATUS.overdue,
  },
  waiting: {
    pending_reply: REFERRAL_STATUS.pending_reply,
  },
  finished: {
    completed: REFERRAL_STATUS.completed,
    rejected: REFERRAL_STATUS.rejected,
  },
} as const;

type OptionValue = {
  [K in keyof typeof OPTIONS]: {
    [L in keyof (typeof OPTIONS)[K]]: (typeof OPTIONS)[K][L];
  }[keyof (typeof OPTIONS)[K]];
}[keyof typeof OPTIONS];

const ReferralsDirectory = () => {
  const dispatch = useAppDispatch();
  const [referralModal, setReferralModal] = useState<boolean>(false);
  const referralRequests = useAppSelector((state) => state.healthRecords.referral_requests);
  const referrals = useAppSelector((state) => state.healthRecords.referral_documents);
  const ownUser = useAppSelector((state) => GetStaff(state));
  const navigateTo: NavigationRef = useRef();
  const [firstRender, setFirstRender] = useState(true);

  const [selectedOption, setOption] = useState<OptionValue>(OPTIONS.pending_action.requests);
  const [selectedDocument, setSelectedDocument] = useState<ReferralRequest | undefined>();

  useFetchPatients(referralRequests);

  const ownClinicId = ownUser?.clinic_id;

  const filteredReferrals = useMemo(() => {
    return referrals.filter(({ clinic_id }) => clinic_id === ownClinicId);
  }, [referrals, ownClinicId]);

  async function onOptionChange(option: OptionValue) {
    setOption(option);
  }

  const counts = useMemo(() => {
    return {
      requests: referralRequests.length,
      pending_send: filteredReferrals.filter(
        (document) => document.referral_status === REFERRAL_STATUS.pending_send,
      ).length,
      pending_reply: filteredReferrals.filter(
        (document) => document.referral_status === REFERRAL_STATUS.pending_reply,
      ).length,
      overdue: filteredReferrals.filter(
        (document) => document.referral_status === REFERRAL_STATUS.overdue,
      ).length,
    };
  }, [referralRequests, filteredReferrals]);

  useEffect(() => {
    dispatch(api.HealthRecords.getReferralRequests());
    dispatch(api.HealthRecords.getPendingReferrals());
  }, [dispatch]);

  useEffect(() => {
    if (selectedOption === REFERRAL_STATUS.rejected) {
      dispatch(api.HealthRecords.getReferralsByStatus({ status: REFERRAL_STATUS.rejected }));
    } else if (selectedOption === REFERRAL_STATUS.completed) {
      dispatch(api.HealthRecords.getReferralsByStatus({ status: REFERRAL_STATUS.completed }));
    }
    if (!firstRender) {
      setSelectedDocument(undefined);
      setTimeout(() => {
        navigateTo.current?.(1);
      }, 100);
    }
  }, [selectedOption, dispatch]);

  function onDocumentSelected(document: ReferralRequest) {
    setSelectedDocument(document);
    if (document) {
      setTimeout(() => {
        navigateTo.current?.(2);
      }, 100);
    }
  }

  useEffect(() => {
    setFirstRender(false);
  }, []);

  let content = null;
  let list = null;

  switch (selectedOption) {
    case OPTIONS.pending_action.requests:
      list = (
        <ListWithDetails
          title="Referral Requests"
          items={referralRequests}
          onClick={onDocumentSelected}
          renderDetails={(request) => <ReferralRequestDetails request={request} />}
          renderBadge={(request) => <ReferralRequestBadge request={request} />}
          //@ts-expect-error
          noDetail
        />
      );

      content = selectedDocument ? <ReferralRequestDetails request={selectedDocument} /> : null;
      break;
    default:
      list = (
        <DocumentListWithDetails
          key={selectedOption}
          allowExpand={true}
          title={selectedOption}
          disableEdit={false}
          documents={filteredReferrals.filter(
            ({ referral_status }) => referral_status === selectedOption,
          )}
          //@ts-expect-error DocumentListWithDetails should be generic
          onClick={onDocumentSelected}
          renderDetails={(document) => <ReferralActions document={document} key={document.id} />}
          noDetail
        />
      );
      content = selectedDocument ? (
        //@ts-expect-error this makes no sense, selectedDocument cannot be a reqref and patdoc
        <ReferralActions document={selectedDocument} key={selectedDocument.id} />
      ) : null;
      break;
  }

  return (
    //@ts-expect-error
    <GenericDirectory title="Referrals" className="referrals-directory">
      {referralModal && (
        <DefaultModal
          wrap={true}
          title="New Referral"
          className="fit-modal new-referral-modal"
          padding={false}
          content={<NewReferralAdmin onClose={() => setReferralModal(false)} />}
          isOpen={referralModal}
          onRequestClose={() => setReferralModal(false)}
        />
      )}
      <ResponsiveColumns
        navigateTo={navigateTo}
        columns={[
          {
            name: 'Menu',
            element: (
              <div className="menu scroll-list">
                <div className="add-button-wrapper">
                  <DefaultButton
                    name="Create Referral"
                    onClick={() => setReferralModal(true)}
                    icon="plus"
                  />
                </div>
                {Object.keys(OPTIONS).map((option) => (
                  <>
                    <span
                      className="small-title capitalize p-3"
                      key={JSON.stringify(OPTIONS[option as keyof typeof OPTIONS])}
                    >
                      {option.replace('_', ' ')}
                    </span>
                    <Menus
                      options={OPTIONS[option as keyof typeof OPTIONS]}
                      selectedOption={selectedOption}
                      onOptionChange={onOptionChange}
                      counts={counts}
                    />
                  </>
                ))}
              </div>
            ),
          },
          list ? { name: 'List', element: <div>{list}</div> } : undefined,
          content ? { name: 'Referral', element: content } : undefined,
        ]}
      />
    </GenericDirectory>
  );
};

interface ReferralActionsProps {
  document: PatientDocument;
}

export const ReferralActions: FC<ReferralActionsProps> = (props) => {
  const dispatch = useAppDispatch();
  const ownId = useAppSelector((state) => state.authentication.user?.id);

  const documentId = props.document.id;

  const existingDocuments = useAppSelector(
    (state) => state.documents.byId[props.document.patient_id!],
  );
  const existingDocument = useMemo(() => {
    return existingDocuments?.find(({ id }) => id === documentId);
  }, [documentId, existingDocuments]);

  const [document, setDocument] = useState<PatientDocument>(existingDocument!);

  const [showDocuments, setShowDocuments] = useState(false);

  const [actionData, setActionData] = useState({ snooze: 21 });
  const [newReferral, setNewReferral] = useState(false);
  const [sendModal, setSendModal] = useState(false);
  const [fax, setFax] = useState<Fax | undefined>(undefined);
  const faxLoading = useIsLoading(api.Fax.getPatientFaxes);

  const isLoading = useIsLoading(api.HealthRecords.updateReferral);

  const patient = useAppSelector((state) => state.patients.byId[document?.patient_id!]);
  const physician = useAppSelector((state) => state.physicians.byId[document?.physician_id!]);

  useEffect(() => {
    if (documentId && !document?.id) {
      dispatch(api.Documents.getDocument({ documentId })).then((result) => {
        if (result) setDocument(result);
      });
    }
  }, [dispatch, documentId, document]);

  async function onSnooze() {
    return saveChanges({
      ...document,
      date_check_referral: moment().add(actionData.snooze, 'days').unix(),
      referral_status: REFERRAL_STATUS.pending_reply,
    });
  }

  async function onSend() {
    setActionData({ ...actionData, snooze: 21 });
    await onSnooze();
  }

  async function markCompleted() {
    return saveChanges({ ...document, referral_status: REFERRAL_STATUS.completed });
  }

  async function markPendingReply() {
    return saveChanges({ ...document, referral_status: REFERRAL_STATUS.pending_reply });
  }

  async function markOverdue() {
    return saveChanges({ ...document, referral_status: REFERRAL_STATUS.overdue });
  }

  async function markRejected(details: string) {
    let changedDocument: PatientDocument = {
      ...document,
      referral_status: REFERRAL_STATUS.rejected,
    };
    if (details) {
      changedDocument = getCommentChanges(changedDocument, details);
    }
    return saveChanges(changedDocument);
  }

  async function saveChanges(newValue: PatientDocument) {
    return dispatch(
      api.HealthRecords.updateReferral({
        id: document.id!,
        patientDocument: newValue,
        patientId: document.patient_id!,
      }),
      { errorTitle: 'Update Referral' },
    ).then((result) => {
      if (result) setDocument(result);
      return result;
    });
  }

  async function fetchFax() {
    let faxes = await dispatch(api.Fax.getPatientFaxes({ patientId: patient?.id! }));
    if (faxes) {
      setFax(faxes.find((fax) => fax.id === document.fax_id));
    }
  }

  function getCommentChanges(value: PatientDocument, comment: string): PatientDocument {
    let originalComments = value.referral_comments;
    let data: TaskComment = {
      author_id: ownId!,
      text: comment,
      date: moment().unix(),
      read_by: [ownId!],
    };
    let newComments = [...(originalComments || []), data];
    return { ...value, referral_comments: newComments };
  }

  async function saveComment(comment: string) {
    let newValue = getCommentChanges(document, comment);
    return saveChanges(newValue);
  }

  function onCall(number: string) {
    dispatch(uiActions.openPhoneScreen({ patientId: patient!.id!, number }));
  }

  const coverPage = document
    ? `Hello,
 
I am writing to follow-up on a referral that was sent over on ${CalendarUtils.format(
        document.date,
      )}. Has an appointment been booked? Patient's name is ${PatientUtils.getName(
        patient,
        undefined,
        false,
      )}.
 
Your response in this matter is greatly appreciated.
 
Thank you,
Care& Family Health`
    : undefined;

  return document ? (
    <div className="referral-actions">
      <div className="main-content full-height-panel underside-right-panel scroll-list">
        <div>
          <span className="small-title">Requesting Practitioner</span>
          <PhysicianBadge physician={physician} />
        </div>
        <div>
          <span className="small-title">Patient</span>
          <PatientBadge patient={patient} goToProfile={true} />
        </div>
        {document.referral?.send_to && (
          <div>
            <span className="small-title">Referral Contact</span>
            <div className="row contact-row">
              {/**@ts-expect-error*/}
              <ContactBadge contact={document.referral.send_to} />
              {!!document.referral?.send_to?.phone && (
                <div>
                  <IconButton
                    onClick={() => onCall(document.referral!.send_to!.phone!)}
                    className="blue"
                  >
                    <Icon
                      height="20px"
                      src="phone"
                      className="white"
                      alt="Start phone call"
                      showTooltip={true}
                      tooltipPlace="left"
                    />
                  </IconButton>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="actions">
          <div className="row">
            <Icon
              src="files-medical"
              alt="See Patient Documents"
              showTooltip={true}
              onClick={() => setShowDocuments(true)}
              height="35px"
              width="35px"
              style={{ margin: 0 }}
            />
            <SmallResourcePreview
              patient={patient}
              title={document.title}
              url={document.filename!}
              getFile={async (dispatchOptions) =>
                dispatch(
                  api.Resource.getResource({ resource: { url: document.filename! } }),
                  dispatchOptions,
                )
              }
            />
            {document.fax_id && (
              <div className="fax-wrapper">
                <DefaultButton
                  name="Show Fax Details"
                  icon="fax"
                  onClick={() => fetchFax()}
                  loading={faxLoading}
                  style={{ width: '180px' }}
                />
              </div>
            )}
            {fax && (
              <FaxPreview
                //@ts-expect-error
                closePreview={() => setFax(undefined)}
                openPreview={!!fax}
                fax={fax}
                onChanged={() => setFax(undefined)}
              />
            )}
          </div>
          <span className="small-title">Actions</span>
          {([REFERRAL_STATUS.pending_reply, REFERRAL_STATUS.overdue] as string[]).includes(
            document.referral_status!,
          ) && (
            <div className="row">
              <DefaultDatePicker
                label="Due date"
                name="date_check_referral"
                value={document.date_check_referral}
                disabled={true}
                style={{ width: '110px' }}
              />
              <DefaultTextField
                name="snooze"
                label="Snooze Days"
                value={actionData.snooze}
                style={{ width: '110px' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  //@ts-expect-error
                  setActionData({ ...actionData, snooze: event.target.value })
                }
                type="number"
                disabled={isLoading}
              />
              <div className="button-wrapper">
                <IconButton
                  onClick={onSnooze}
                  className="blue"
                  //@ts-expect-error
                  disabled={!parseInt(actionData.snooze) || isLoading}
                >
                  <Icon
                    className="white"
                    width="15px"
                    height="15px"
                    src="snooze"
                    alt="Snooze Referral"
                  />
                </IconButton>
              </div>
            </div>
          )}
          <div className="row">
            <DefaultButton
              onClick={() => setSendModal(true)}
              name="Email/Fax"
              icon="paper-plane"
              style={{ width: '150px' }}
              disabled={isLoading}
            />
            <DefaultButton
              onClick={() => setNewReferral(true)}
              name="New Referral"
              icon="user-doctor"
              style={{ width: '150px' }}
              disabled={isLoading}
            />
            {sendModal && (
              <DefaultModal
                wrap={true}
                title="Send Documents"
                className="large-modal send-multiple-documents-modal"
                padding={false}
                content={
                  <SendMultipleDocuments
                    //@ts-expect-error
                    patient={patient}
                    onClose={() => setSendModal(false)}
                    documents={[document]}
                    fax={document.referral?.send_to?.fax}
                    email={document.referral?.send_to?.email}
                    documentId={document.id}
                    emailBody={coverPage}
                    emailSubject="Referral Status Update"
                    coverPage={coverPage}
                    onSend={onSend}
                  />
                }
                isOpen={true}
                onRequestClose={() => setSendModal(false)}
              />
            )}
            {newReferral && (
              <DefaultModal
                wrap={true}
                title="New Referral"
                className="fit-modal new-referral-modal"
                padding={false}
                content={
                  <NewReferralAdmin
                    patient={patient}
                    document={{
                      ...document,
                      referral: {
                        ...document.referral,
                        referral_comments: document.referral_comments,
                      },
                    }}
                    onClose={() => setNewReferral(false)}
                  />
                }
                isOpen={newReferral}
                onRequestClose={() => setNewReferral(false)}
              />
            )}
          </div>
          <div className="row">
            <ConfirmationButton
              onClick={(e, data) => markRejected(data!.Details!)}
              name="Mark Rejected"
              className="red"
              style={{ width: '150px' }}
              disabled={isLoading}
              hide={document.referral_status === REFERRAL_STATUS.rejected}
              confirmation={{
                message: 'Please type any details regarding this rejection',
                form: [{ type: 'textField', label: 'Details' }] as const,
              }}
            />
            <DefaultButton
              onClick={markCompleted}
              name="Mark Completed"
              className="blue"
              style={{ width: '150px' }}
              disabled={isLoading}
              hide={document.referral_status === REFERRAL_STATUS.completed}
            />
            <DefaultButton
              onClick={markOverdue}
              name="Mark Overdue"
              style={{ width: '150px' }}
              disabled={isLoading}
              hide={
                !([REFERRAL_STATUS.completed, REFERRAL_STATUS.rejected] as string[]).includes(
                  document.referral_status!,
                )
              }
            />
            <DefaultButton
              onClick={markPendingReply}
              name="Mark Pending Reply"
              style={{ width: '200px' }}
              disabled={isLoading}
              hide={
                !([REFERRAL_STATUS.completed, REFERRAL_STATUS.rejected] as string[]).includes(
                  document.referral_status!,
                )
              }
            />
          </div>
        </div>
      </div>
      <Comments document={document} saveComment={saveComment} ownId={ownId!} />
      {showDocuments && (
        <DefaultModal
          wrap={true}
          title="Email Documents"
          className="large-modal send-multiple-documents-modal"
          padding={false}
          content={
            <SendMultipleDocuments
              //@ts-expect-error
              patient={patient}
              onClose={() => setShowDocuments(false)}
              hideContacts={true}
              hideToggle={true}
              hideSend={true}
            />
          }
          isOpen={showDocuments}
          onRequestClose={() => setShowDocuments(false)}
        />
      )}
    </div>
  ) : (
    <ProgressMessage size={50} className="m-5" />
  );
};

interface CommentsProps {
  document: PatientDocument;
  saveComment: (comment: string) => Promise<PatientDocument | undefined>;
  submitting?: boolean;
  ownId: string;
}

let messagesEnd: Element | null;

const Comments: FC<CommentsProps> = ({ document, saveComment, submitting, ownId }) => {
  const dispatch = useAppDispatch();

  const admins = useAppSelector((state) => state.admins.byId);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const [currentComment, setComment] = usePersistedState(
    'clear.' + document.id + '-comment',
    '',
    true,
  );

  const [documentModal, setDocumentModal] = useState<PatientDocument | undefined>(undefined);
  const [commentFilter, setCommentFilter] = useState<'staff' | 'system' | 'all'>('staff');

  const loadingDocument = useIsLoading(api.Documents.getDocument);

  useEffect(() => {
    scrollToBottom();
  }, []);

  function setMessagesEnd(element: HTMLElement | null) {
    messagesEnd = element;
  }

  function scrollToBottom() {
    if (messagesEnd) messagesEnd.scrollIntoView();
  }

  function handleTypedCommentChange(event: ChangeEvent<HTMLInputElement>) {
    setComment(event.target.value);
  }

  async function iconClicked() {
    return saveCommentWrapper(currentComment);
  }

  async function openDocument(documentId: string) {
    let document = await dispatch(api.Documents.getDocument({ documentId }));
    setDocumentModal(document);
  }

  function onKeyDown(
    event: React.KeyboardEvent<HTMLInputElement>,
    currentMesssage = currentComment,
  ) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      saveCommentWrapper(currentMesssage);
    }
  }

  async function saveCommentWrapper(comment: string) {
    if (!!comment && !submitting) {
      setComment('');
      let result = await saveComment(comment);
      if (!result) setComment(comment);
    }
  }

  // System-generated comments (status changes, automated activity) carry an author_id that
  // doesn't resolve to a real admin/physician; staff comments do. Use that to split them.
  const isStaffComment = (comment: TaskComment) =>
    !!(admins[comment.author_id] || physicians[comment.author_id]);

  const allComments = document.referral_comments || [];
  const staffCount = allComments.filter(isStaffComment).length;
  const systemCount = allComments.length - staffCount;

  let comments = [...allComments].filter((comment) => {
    if (commentFilter === 'all') return true;
    if (commentFilter === 'staff') return isStaffComment(comment);
    return !isStaffComment(comment);
  });

  return (
    <div className="comments-panel full-height-panel underside-right-panel scroll-list">
      <div className="small-title">Comments</div>
      <div className="comment-filter">
        {(['all', 'staff', 'system'] as const).map((option) => (
          <button
            key={option}
            type="button"
            className={commentFilter === option ? 'active' : ''}
            onClick={() => setCommentFilter(option)}
          >
            {option === 'staff' ? 'Staff' : option === 'system' ? 'System' : 'All'}
            {option === 'staff' && <UnreadBadge unreadCount={staffCount} squished />}
            {option === 'system' && <UnreadBadge unreadCount={systemCount} squished />}
          </button>
        ))}
      </div>
      {documentModal && (
        <DocumentDetailsModal
          document={documentModal}
          onClose={() => setDocumentModal(undefined)}
          isOpen={!!documentModal}
        />
      )}
      <div className="comment-list scroll-list">
        {comments.length === 0 && <span className="no-comments">No comments found</span>}
        {comments.map((comment, index) => {
          let user = admins[comment.author_id] || physicians[comment.author_id];
          const name = StaffUtils.getName(user) || comment.author_id;
          const accent = StringUtils.colorFromString(comment.author_id);
          const initials = StringUtils.getInitials(name).slice(0, 2) || '?';
          const photo = user?.photo;
          let className = 'comment';
          let isOwn = ownId === comment.author_id;
          if (isOwn) className += ' own';
          return (
            <div className={className} key={JSON.stringify(comment)}>
              <div className="comment-header">
                <div className="avatar" style={photo ? undefined : { background: accent }}>
                  {photo ? <img src={photo} alt={name} /> : initials}
                </div>
                <div className="meta">
                  <div className="author">{name}</div>
                  <div className="date">
                    <span>{CalendarUtils.format(comment.date, 'DD/MM/YYYY h:mm A')}</span>
                    {comment.email_content && (
                      <div className="email-content">
                        <NumberBadge
                          id={`tooltip-${index}`}
                          popupContent={ChatUtils.cleanMessageContent(comment.email_content, false)}
                          number={<Icon src="eye" />}
                        />
                      </div>
                    )}
                    {comment.document_id && (
                      <div className="document" onClick={() => openDocument(comment.document_id!)}>
                        <Icon src="file-pdf" width="15px" height="15px" loading={loadingDocument} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="text">{(comment || {}).text}</div>
            </div>
          );
        })}
        <div style={{ float: 'left', clear: 'both' }} ref={(el) => setMessagesEnd(el)} />
      </div>
      <div className="comment-box">
        <div className="input-bar" onKeyDown={onKeyDown} tabIndex={0}>
          <ContentEditable
            key="send_message-bar"
            name="send_message-bar"
            disabled={true}
            onChange={handleTypedCommentChange}
            text={currentComment}
          />
          <div className="icons">
            <Icon
              id="send_message-button"
              src="paper-plane"
              alt="Send"
              showTooltip={true}
              className="icon-submit"
              height="17px"
              width="17px"
              onClick={iconClicked}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

interface MenusProps {
  options: Record<string, string>;
  selectedOption: OptionValue;
  onOptionChange: (name: OptionValue) => void;
  counts: Record<string, number>;
}

const Menus: FC<MenusProps> = ({ options, selectedOption, onOptionChange, counts = {} }) => {
  let menuItems = Object.keys(options).map((optionName) => {
    let name = options[optionName];
    let key = optionName;
    let count = counts[key] || 0;

    return (
      <SelectableListItem
        key={key}
        divider={false}
        selected={selectedOption === name}
        onClick={() => onOptionChange(name as OptionValue)}
        item={
          <span>
            {name}
            {count > 0 ? (
              <UnreadBadge
                unreadCount={count}
                style={{ marginLeft: '10px', display: 'inline-block' }}
              />
            ) : null}
          </span>
        }
      />
    );
  });
  return <List>{menuItems}</List>;
};

export default ReferralsDirectory;
